import { translitToRus } from 'app/utils';
import { createSelector } from 'reselect';
const transportState = (state) => state.transport;
export const itemsSelector = createSelector(transportState, (transport) => transport.items);
export const selectedItemSelector = createSelector(transportState, (transport) => transport.selectedItem);
export const checkedItemsSelector = createSelector(transportState, (transport) => transport.checkedItems);
export const transportColumnCheckedItemsSelector = createSelector(transportState, (transport) => transport.transportColumnCheckedItems);
const isTransportColumnAccessible = (column, roleId) => !column.roles || column.roles.some((role) => config.userRoles[role] === roleId);
export const transportMenuColumnsSelector = createSelector([(state) => { var _a; return (_a = state.session.session) === null || _a === void 0 ? void 0 : _a.role_id; }], (roleId) => config.transportColumns.filter((column) => isTransportColumnAccessible(column, roleId)));
export const visibleTransportColumnsSelector = createSelector([transportColumnCheckedItemsSelector, (state) => { var _a; return (_a = state.session.session) === null || _a === void 0 ? void 0 : _a.role_id; }], (checkedItems, roleId) => config.transportColumns.filter((column, index) => index === 0 || (checkedItems.indexOf(column.name) !== -1 && isTransportColumnAccessible(column, roleId))));
export const searchValueSelector = createSelector(transportState, (transport) => transport.searchValue);
export const inspectionSelectedItemSelector = createSelector(transportState, (transport) => transport.inspectionSelectedItem);
export const groupItemsSelector = createSelector(transportState, (transport) => transport.groupItems);
export const groupSelectedItemSelector = createSelector(transportState, (transport) => transport.groupSelectedItem);
export const groupSearchValueSelector = createSelector(transportState, (transport) => transport.groupSearchValue);
export const checkListSelectedItemSelector = createSelector(transportState, (transport) => transport.checkListSelectedItem);
export const sortSelectedItemSelector = createSelector(transportState, (transport) => transport.sortSelectedItem);
function compareTransportListItems(a, b, sortSelectedItem) {
    switch (sortSelectedItem.name) {
        case 'name':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return a.name.localeCompare(b.name);
                case 'DESC':
                    return b.name.localeCompare(a.name);
            }
            break;
        case 'group_name':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return b.group_name === null ? -1 : a.group_name === null ? 1 : (a.group_name || '').localeCompare(b.group_name || '');
                case 'DESC':
                    return b.group_name === null ? -1 : a.group_name === null ? 1 : (b.group_name || '').localeCompare(a.group_name || '');
            }
            break;
        case 'vin':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return b.vin === null ? -1 : a.vin === null ? 1 : (a.vin || '').localeCompare(b.vin || '');
                case 'DESC':
                    return b.vin === null ? -1 : a.vin === null ? 1 : (b.vin || '').localeCompare(a.vin || '');
            }
            break;
        case 'imei':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return b.imei === null ? -1 : a.imei === null ? 1 : (a.imei || '').localeCompare(b.imei || '');
                case 'DESC':
                    return b.imei === null ? -1 : a.imei === null ? 1 : (b.imei || '').localeCompare(a.imei || '');
            }
            break;
        case 'comment':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return (a.comment || '').localeCompare(b.comment || '');
                case 'DESC':
                    return (b.comment || '').localeCompare(a.comment || '');
            }
            break;
        case 'number':
            switch (sortSelectedItem.order) {
                case 'ASC':
                    return b.number == null
                        ? -1
                        : a.number == null
                            ? 1
                            : String(a.number).localeCompare(String(b.number));
                case 'DESC':
                    return b.number == null
                        ? -1
                        : a.number == null
                            ? 1
                            : String(b.number).localeCompare(String(a.number));
            }
            break;
        default: {
            const aValue = a[sortSelectedItem.name];
            const bValue = b[sortSelectedItem.name];
            switch (typeof aValue) {
                case 'string':
                    switch (sortSelectedItem.order) {
                        case 'ASC':
                            return String(aValue || '').localeCompare(String(bValue || ''));
                        case 'DESC':
                            return String(bValue || '').localeCompare(String(aValue || ''));
                    }
                    break;
                case 'number':
                    switch (sortSelectedItem.order) {
                        case 'ASC':
                            return aValue - bValue;
                        case 'DESC':
                            return bValue - aValue;
                    }
                    break;
            }
        }
    }
    return 0;
}
export const sortItemsSelector = createSelector([itemsSelector, sortSelectedItemSelector, searchValueSelector, transportColumnCheckedItemsSelector], (items, sortSelectedItem, searchValue) => {
    const compareFn = (a, b) => compareTransportListItems(a, b, sortSelectedItem);
    if (searchValue.length) {
        const normalizedSearchValue = translitToRus(searchValue).toLowerCase();
        switch (sortSelectedItem.name) {
            case 'vin':
                return items
                    .filter((item) => (item.vin || '').toLowerCase().indexOf(searchValue.toLowerCase()) !== -1)
                    .sort(compareFn);
            case 'imei':
                return items
                    .filter((item) => (item.imei || '').toLowerCase().indexOf(searchValue.toLowerCase()) !== -1)
                    .sort(compareFn);
            case 'name':
                return items
                    .filter((item) => translitToRus(item.name || '').toLowerCase().indexOf(normalizedSearchValue) !== -1)
                    .sort(compareFn);
            case 'group_name':
                return items
                    .filter((item) => translitToRus(item.group_name || '').toLowerCase().indexOf(normalizedSearchValue) !== -1)
                    .sort(compareFn);
            case 'number':
                return items
                    .filter((item) => translitToRus(item.number || '').toLowerCase().indexOf(normalizedSearchValue) !== -1)
                    .sort(compareFn);
            case 'volume':
                return items.filter((item) => (item.volume || '').toString().indexOf(searchValue) !== -1).sort(compareFn);
            case 'comment':
                return items
                    .filter((item) => translitToRus(item.comment || '').toLowerCase().indexOf(normalizedSearchValue) !== -1)
                    .sort(compareFn);
            default:
                return items.sort(compareFn);
        }
    }
    return items.sort(compareFn);
});
export const sortItemsGroupSelector = createSelector([groupItemsSelector, groupSearchValueSelector], (groupItems, groupSearchValue) => {
    const compareFn = (a, b) => a.name.localeCompare(b.name);
    if (groupSearchValue.length) {
        return groupItems
            .filter((item) => translitToRus(item.name).indexOf(translitToRus(groupSearchValue)) !== -1)
            .sort(compareFn);
    }
    return groupItems.sort(compareFn);
});
export const divisionItemsTransportSelector = createSelector(transportState, (transport) => transport.divisionItems);
export const autocolonItemsTransportSelector = createSelector(transportState, (transport) => transport.autocolonItems);
export const autocolonNotSelectedItemsTransportSelector = createSelector(transportState, (transport) => transport.autocolonItems.filter((item) => !item.id_division));
export const autocolonSearchValueTransportSelector = createSelector(transportState, (transport) => {
    return transport.autocolonSearchValue;
});
export const autocolonSelectedItemTransportSelector = createSelector(transportState, (transport) => transport.autocolonSelectedItem);
export const autocolonGeofenceCheckedIdTransportSelector = createSelector(transportState, (transport) => { var _a; return (((_a = transport === null || transport === void 0 ? void 0 : transport.autocolonSelectedItem) === null || _a === void 0 ? void 0 : _a.geofenceItems) || []); });
export const divisionAutocolnSortedItemsTransportSelector = createSelector([divisionItemsTransportSelector, autocolonItemsTransportSelector, transportState], (divisionItems, autocolonItems, transport) => {
    const autocolonSearchValue = transport.autocolonSearchValue;
    if (autocolonSearchValue) {
        return divisionItems
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((item) => {
            const autocolons = autocolonItems
                .filter((autocolonItem) => autocolonItem.id_division === item.id)
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((autocolonItem) => {
                return autocolonItem.name.toLowerCase().match(autocolonSearchValue.toLowerCase() || '');
            });
            return Object.assign(Object.assign({}, item), { autocolons });
        })
            .concat({
            name: 'Не распределено',
            id: 0,
            autocolons: autocolonItems
                .filter((autocolonItem) => !autocolonItem.id_division)
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((autocolonItem) => autocolonItem.name.toLowerCase().match(autocolonSearchValue.toLowerCase() || '')),
        });
    }
    return divisionItems
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => {
        const autocolons = autocolonItems
            .filter((autocolonItem) => autocolonItem.id_division === item.id)
            .sort((a, b) => a.name.localeCompare(b.name));
        return Object.assign(Object.assign({}, item), { autocolons });
    })
        .concat({
        name: 'Не распределено',
        id: 0,
        autocolons: autocolonItems
            .filter((autocolonItem) => !autocolonItem.id_division)
            .sort((a, b) => a.name.localeCompare(b.name)),
    });
});
